// INTEZ TELEPHONY MANAGED ROUTING V15.7.3
// INTEZ TELEPHONY PREVIEW UX FIX V15.7.5
// INTEZ TELEPHONY APPLY UX V15.7.8
// INTEZ TELEPHONY MEMBER ORDER UX V15.7.10
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  CircleAlert,
  Eye,
  GripVertical,
  Headphones,
  LoaderCircle,
  Network,
  PhoneIncoming,
  Plus,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';
import { telephonyApi } from '../telephonyApi';
import { moveRouteMember, reorderRouteMember } from '../RouteMemberOrder';
import type { TelephonyRouteGroup, TelephonyRoutePreview, TelephonyRouteStrategy } from '../types';
import { RoutingDiffView } from './RoutingDiffView';
import { RoutingHistoryPanel } from './RoutingHistoryPanel';

function strategyLabel(strategy: TelephonyRouteStrategy) {
  return strategy === 'SEQUENTIAL' ? 'Последовательно' : 'Всем одновременно';
}

function formatDate(value?: string | null) {
  if (!value) return 'ещё не применялось';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
}

export function TelephonyRoutesPanel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [strategy, setStrategy] = useState<TelephonyRouteStrategy>('RING_ALL');
  const [ringSeconds, setRingSeconds] = useState(20);
  const [maxWaitSeconds, setMaxWaitSeconds] = useState(35);
  const [fallbackQueueId, setFallbackQueueId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [draggedMemberId, setDraggedMemberId] = useState('');
  const [dragOverMemberId, setDragOverMemberId] = useState('');
  const [preview, setPreview] = useState<TelephonyRoutePreview | null>(null);
  const [applyPreview, setApplyPreview] = useState<TelephonyRoutePreview | null>(null);
  const [applyError, setApplyError] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const routesQuery = useQuery({
    queryKey: ['telephony-routes'],
    queryFn: telephonyApi.routes,
    enabled: userHasRole(user, 'ADMIN'),
  });

  const usersQuery = useQuery({
    queryKey: ['telephony-browser-users'],
    queryFn: telephonyApi.users,
    enabled: userHasRole(user, 'ADMIN'),
  });

  const historyQuery = useQuery({
    queryKey: ['telephony-routing-history'],
    queryFn: telephonyApi.routesHistory,
    enabled: userHasRole(user, 'ADMIN'),
  });

  const groups = routesQuery.data?.groups || [];
  const selected = groups.find(group => group.id === selectedId) || groups[0] || null;
  const root = groups.find(group => group.id === routesQuery.data?.rootQueueId) || null;

  useEffect(() => {
    if (!selectedId && groups[0]) setSelectedId(groups[0].id);
    if (selectedId && !groups.some(group => group.id === selectedId) && groups[0]) setSelectedId(groups[0].id);
  }, [groups, selectedId]);

  useEffect(() => {
    if (!selected) return;
    setName(selected.name);
    setStrategy(selected.strategy);
    setRingSeconds(selected.ringSeconds);
    setMaxWaitSeconds(selected.maxWaitSeconds);
    setFallbackQueueId(selected.fallbackQueueId || '');
    setIsActive(selected.isActive);
    setMemberIds([...selected.members].sort((a, b) => a.sortOrder - b.sortOrder).map(member => member.telephonyUserId));
    setDraggedMemberId('');
    setDragOverMemberId('');
    setError('');
    setMessage('');
  }, [selected]);

  const phoneUsers = useMemo(
    () => (usersQuery.data || []).filter(item => item.telephony_user_id),
    [usersQuery.data],
  );

  const userByTelephonyId = useMemo(
    () => new Map(phoneUsers.map(item => [String(item.telephony_user_id), item] as const)),
    [phoneUsers],
  );

  const routeMemberNames = useMemo(
    () => new Map(phoneUsers.map(item => [String(item.telephony_user_id), String(item.full_name || item.extension || 'Сотрудник')] as const)),
    [phoneUsers],
  );

  const orderedPhoneUsers = useMemo(() => {
    const selectedOrder = new Map(memberIds.map((id, index) => [id, index] as const));
    return phoneUsers
      .map((item, sourceIndex) => ({ item, sourceIndex }))
      .sort((left, right) => {
        const leftOrder = selectedOrder.get(String(left.item.telephony_user_id));
        const rightOrder = selectedOrder.get(String(right.item.telephony_user_id));
        if (leftOrder !== undefined && rightOrder !== undefined) return leftOrder - rightOrder;
        if (leftOrder !== undefined) return -1;
        if (rightOrder !== undefined) return 1;
        return left.sourceIndex - right.sourceIndex;
      })
      .map(({ item }) => item);
  }, [memberIds, phoneUsers]);

  const chain = useMemo(() => {
    const result: TelephonyRouteGroup[] = [];
    const seen = new Set<string>();
    let current: TelephonyRouteGroup | null = root;
    while (current && !seen.has(current.id) && result.length < 8) {
      seen.add(current.id);
      result.push(current);
      current = groups.find(group => group.id === current?.fallbackQueueId) || null;
    }
    return result;
  }, [groups, root]);

  const refresh = async () => {
    await Promise.all([
      routesQuery.refetch(),
      usersQuery.refetch(),
      queryClient.invalidateQueries({ queryKey: ['telephony-browser-diagnostics'] }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: () => telephonyApi.createRouteGroup({
      name: `Новая группа ${groups.length + 1}`,
      strategy: 'RING_ALL',
      ringSeconds: 20,
      maxWaitSeconds: 35,
      isActive: true,
    }),
    onSuccess: data => {
      const previousIds = new Set(groups.map(group => group.id));
      const created = data.groups.find(group => !previousIds.has(group.id)) || data.groups[data.groups.length - 1];
      if (created) setSelectedId(created.id);
      setMessage('Группа создана в черновике. FreeSWITCH пока не изменён.');
      setError('');
      queryClient.setQueryData(['telephony-routes'], data);
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось создать группу'),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Группа не выбрана');
      await telephonyApi.updateRouteGroup(selected.id, {
        name: name.trim(),
        strategy,
        ringSeconds,
        maxWaitSeconds,
        ...(fallbackQueueId ? { fallbackQueueId } : { clearFallback: true }),
        isActive,
      });
      return telephonyApi.updateRouteMembers(selected.id, memberIds);
    },
    onSuccess: data => {
      queryClient.setQueryData(['telephony-routes'], data);
      setMessage('Черновик группы сохранён. Рабочий маршрут не менялся.');
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось сохранить группу'),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Группа не выбрана');
      if (!window.confirm(`Удалить группу «${selected.name}» из черновика?`)) return null;
      return telephonyApi.deleteRouteGroup(selected.id);
    },
    onSuccess: data => {
      if (!data) return;
      queryClient.setQueryData(['telephony-routes'], data);
      setSelectedId(data.groups[0]?.id || '');
      setMessage('Группа удалена из черновика. Рабочий маршрут не менялся.');
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось удалить группу'),
  });

  const rootMutation = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Группа не выбрана');
      return telephonyApi.setRootRoute(selected.id);
    },
    onSuccess: data => {
      queryClient.setQueryData(['telephony-routes'], data);
      setMessage('Основная входящая группа изменена в черновике.');
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось изменить основной маршрут'),
  });

  const previewMutation = useMutation({
    mutationFn: telephonyApi.previewRoutes,
    onMutate: () => {
      setError('');
      setMessage('');
    },
    onSuccess: data => {
      setPreview(data);
      setError('');
    },
    onError: reason => setError(reason instanceof Error ? reason.message : 'Не удалось построить предпросмотр'),
  });

  const prepareApplyMutation = useMutation({
    mutationFn: telephonyApi.previewRoutes,
    onMutate: () => {
      setApplyError('');
      setError('');
      setMessage('');
    },
    onSuccess: check => {
      if (check.validation.errors.length) {
        const text = check.validation.errors.join(' ');
        setError(text);
        setApplyError(text);
        return;
      }
      setApplyPreview(check);
    },
    onError: reason => {
      const text = reason instanceof Error ? reason.message : 'Не удалось проверить маршрутизацию перед применением';
      setError(text);
      setApplyError(text);
    },
  });

  const applyMutation = useMutation({
    mutationFn: telephonyApi.applyRoutes,
    onMutate: () => {
      setApplyError('');
      setError('');
      setMessage('');
    },
    onSuccess: async data => {
      queryClient.setQueryData(['telephony-routes'], data.routes);
      void queryClient.invalidateQueries({ queryKey: ['telephony-routing-history'] });
      setApplyPreview(null);
      setPreview(data.preview);
      setMessage('Маршрутизация применена. FreeSWITCH и регистрация Билайн подтверждены.');
      setError('');
      await refresh();
    },
    onError: reason => {
      const text = reason instanceof Error ? reason.message : 'Не удалось применить маршрутизацию';
      if (text.includes('Черновик маршрута изменился после предпросмотра')) {
        setApplyPreview(null);
        prepareApplyMutation.mutate();
        return;
      }
      setApplyError(text);
      setError(text);
    },
  });

  const toggleMember = (id: string) => {
    const target = userByTelephonyId.get(id);
    if (!target?.browser_phone_enabled || !target.allow_inbound || !target.extension) return;
    setMemberIds(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
    setMessage('');
  };

  const moveMember = (index: number, delta: number) => {
    setMemberIds(current => moveRouteMember(current, index, delta < 0 ? -1 : 1));
    setMessage('');
  };

  const finishMemberDrag = () => {
    setDraggedMemberId('');
    setDragOverMemberId('');
  };

  const dropMember = (targetId: string) => {
    if (!draggedMemberId) return;
    setMemberIds(current => reorderRouteMember(current, draggedMemberId, targetId));
    setMessage('');
    finishMemberDrag();
  };

  if (!userHasRole(user, 'ADMIN')) {
    return <section className="surface telephony-stage-panel telephony-stage-access">
      <Network />
      <div><h3>Группы и маршруты</h3><p>Изменение маршрутизации доступно администратору.</p></div>
    </section>;
  }

  const isRoot = Boolean(selected && selected.id === routesQuery.data?.rootQueueId);
  const canDelete = Boolean(selected && !isRoot);
  const pending = Boolean(routesQuery.data?.hasUnappliedChanges);
  const legacy = routesQuery.data?.mode === 'LEGACY';

  return <div className="telephony-routes-v15">
    <section className="surface telephony-stage-panel">
      <div className="telephony-stage-panel-head telephony-route-editor-head">
        <div><span className="telephony-stage-icon"><Network /></span><div><small>МАРШРУТИЗАЦИЯ</small><h2>Группы и маршруты</h2><p>Изменения сначала сохраняются как черновик. FreeSWITCH меняется только после отдельного применения.</p></div></div>
        <div className="telephony-route-actions">
          <button type="button" className="button" onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
            {previewMutation.isPending ? <LoaderCircle className="spin" /> : <Eye />}
            {previewMutation.isPending ? 'Формируем…' : 'Предпросмотр'}
          </button>
          <button
            type="button"
            className="button primary"
            onClick={() => prepareApplyMutation.mutate()}
            disabled={prepareApplyMutation.isPending || applyMutation.isPending || routesQuery.isLoading || (!legacy && !pending)}
          >
            {prepareApplyMutation.isPending || applyMutation.isPending ? <LoaderCircle className="spin" /> : <ShieldCheck />}
            {prepareApplyMutation.isPending
              ? 'Проверяем…'
              : applyMutation.isPending
                ? 'Применяем…'
                : legacy
                  ? 'Зафиксировать схему'
                  : pending
                    ? 'Применить изменения'
                    : 'Применено'}
          </button>
        </div>
      </div>

      <div className={`telephony-route-draft-state ${pending || legacy ? 'warning' : 'ok'}`}>
        {pending || legacy ? <CircleAlert /> : <CheckCircle2 />}
        <span>
          <b>{legacy ? 'Работает стабильная схема v15.6.4' : pending ? 'Есть неприменённые изменения' : 'Черновик совпадает с рабочим маршрутом'}</b>
          <small>
            {legacy
              ? 'Редактор уже доступен, но FreeSWITCH пока использует прежнюю одновременную группу. Нажмите «Зафиксировать схему», когда будете готовы.'
              : pending
                ? `Черновик r${routesQuery.data?.draftRevision ?? 0}; применено r${routesQuery.data?.appliedRevision ?? 0}.`
                : `Последнее применение: ${formatDate(routesQuery.data?.appliedAt)}.`}
          </small>
        </span>
      </div>

      <div className="telephony-route-live-flow">
        <article><PhoneIncoming /><span><small>Входящий канал</small><b>Билайн</b><em>Общий системный trunk DO0</em></span></article>
        <ArrowRight />
        <article className="accent"><Users /><span><small>Основная группа</small><b>{root?.name || 'Не выбрана'}</b><em>{root ? strategyLabel(root.strategy) : 'Настройте маршрут'}</em></span></article>
        <ArrowRight />
        <article><Headphones /><span><small>Получатели</small><b>{root?.members.filter(member => member.eligible).length || 0} доступно</b><em>{chain.length > 1 ? `Резерв: ${chain.slice(1).map(group => group.name).join(' → ')}` : 'Без резервного маршрута'}</em></span></article>
      </div>
    </section>

    {error && <div className="form-error telephony-route-message">{error}</div>}
    {message && <div className="form-success telephony-route-message"><CheckCircle2 /> {message}</div>}

    <div className="telephony-route-editor-grid">
      <section className="surface telephony-route-groups">
        <div className="telephony-route-section-head">
          <div><small>ГРУППЫ</small><h3>Группы вызова</h3></div>
          <button type="button" className="button compact" onClick={() => createMutation.mutate()} disabled={createMutation.isPending}><Plus /> Добавить</button>
        </div>
        <div className="telephony-route-group-list">
          {groups.map(group => <button type="button" key={group.id} className={`telephony-route-group-card ${selected?.id === group.id ? 'active' : ''}`} onClick={() => setSelectedId(group.id)}>
            <span className="telephony-route-group-icon"><Users /></span>
            <span><b>{group.name}</b><small>{strategyLabel(group.strategy)} · {group.members.length} сотрудников</small></span>
            <span className="telephony-route-group-badges">
              {group.id === routesQuery.data?.rootQueueId && <i>Основная</i>}
              {!group.isActive && <i className="muted">Отключена</i>}
            </span>
          </button>)}
          {!routesQuery.isLoading && !groups.length && <div className="telephony-stage-empty"><Users /><span><b>Групп пока нет</b><small>Создайте первую группу вызова.</small></span></div>}
        </div>
      </section>

      <section className="surface telephony-route-editor">
        {selected ? <>
          <div className="telephony-route-section-head">
            <div><small>НАСТРОЙКИ ГРУППЫ</small><h3>{selected.name}</h3></div>
            <div className="telephony-route-inline-actions">
              {!isRoot && <button type="button" className="button compact" onClick={() => rootMutation.mutate()} disabled={rootMutation.isPending}><PhoneIncoming /> Сделать основной</button>}
              <button type="button" className="icon-button danger" title="Удалить группу" disabled={!canDelete || deleteMutation.isPending} onClick={() => deleteMutation.mutate()}><Trash2 /></button>
            </div>
          </div>

          <div className="telephony-route-form">
            <label><span>Название</span><input value={name} onChange={event => setName(event.target.value)} maxLength={120} /></label>
            <label><span>Стратегия вызова</span><select value={strategy} onChange={event => setStrategy(event.target.value as TelephonyRouteStrategy)}><option value="RING_ALL">Всем одновременно</option><option value="SEQUENTIAL">Последовательно</option></select></label>
            <label><span>Время звонка сотруднику, сек.</span><input type="number" min={5} max={120} value={ringSeconds} onChange={event => setRingSeconds(Math.max(5, Math.min(120, Number(event.target.value) || 5)))} /></label>
            <label><span>Общий тайм-аут маршрута, сек.</span><input type="number" min={5} max={600} value={maxWaitSeconds} onChange={event => setMaxWaitSeconds(Math.max(5, Math.min(600, Number(event.target.value) || 5)))} /></label>
            <label><span>Резервная группа</span><select value={fallbackQueueId} onChange={event => setFallbackQueueId(event.target.value)}><option value="">Нет</option>{groups.filter(group => group.id !== selected.id).map(group => <option key={group.id} value={group.id}>{group.name}</option>)}</select></label>
            <label className="telephony-route-active-switch"><span>Состояние</span><span><input type="checkbox" checked={isActive} onChange={event => setIsActive(event.target.checked)} /> <b>{isActive ? 'Группа включена' : 'Группа отключена'}</b></span></label>
          </div>

          <div className="telephony-route-members-head">
            <div><small>СОТРУДНИКИ</small><h4>Получатели звонка</h4><p>{strategy === 'SEQUENTIAL' ? 'Перетащите выбранного сотрудника или используйте стрелки: порядок сверху вниз определяет очередность вызова.' : 'Все выбранные сотрудники получают вызов одновременно.'}</p></div>
            <span>{memberIds.length} выбрано</span>
          </div>

          <div className="telephony-route-members">
            {orderedPhoneUsers.map(item => {
              const id = String(item.telephony_user_id);
              const checked = memberIds.includes(id);
              const position = memberIds.indexOf(id);
              const canUse = Boolean(item.browser_phone_enabled && item.allow_inbound && item.extension);
              return <article
                key={item.portal_user_id}
                className={`${!canUse ? 'disabled' : ''} ${draggedMemberId === id ? 'dragging' : ''} ${dragOverMemberId === id ? 'drag-over' : ''}`.trim()}
                onDragOver={event => {
                  if (!checked || strategy !== 'SEQUENTIAL' || !draggedMemberId || draggedMemberId === id) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDragOverMemberId(id);
                }}
                onDrop={event => {
                  if (!checked || strategy !== 'SEQUENTIAL') return;
                  event.preventDefault();
                  dropMember(id);
                }}
              >
                <label>
                  <input type="checkbox" checked={checked} disabled={!canUse} onChange={() => toggleMember(id)} />
                  <i>{(item.full_name || '?').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()}</i>
                  <span><b>{item.full_name}</b><small>доб. {item.extension || '—'} · {canUse ? 'готов к входящим' : !item.browser_phone_enabled ? 'телефон отключён' : 'входящие запрещены'}</small></span>
                </label>
                {checked && strategy === 'SEQUENTIAL' && <span className="telephony-route-order">
                  <button
                    type="button"
                    className="telephony-route-drag-handle"
                    draggable
                    aria-label={`Перетащить сотрудника ${item.full_name}`}
                    title="Перетащить на другую позицию"
                    onDragStart={event => {
                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData('text/plain', id);
                      setDraggedMemberId(id);
                      setDragOverMemberId('');
                    }}
                    onDragEnd={finishMemberDrag}
                  ><GripVertical /></button>
                  <b>#{position + 1}</b>
                  <button type="button" className="icon-button" disabled={position <= 0} onClick={() => moveMember(position, -1)}><ArrowUp /></button>
                  <button type="button" className="icon-button" disabled={position < 0 || position >= memberIds.length - 1} onClick={() => moveMember(position, 1)}><ArrowDown /></button>
                </span>}
              </article>;
            })}
          </div>

          <div className="telephony-route-editor-footer">
            <span><RotateCcw /> Сохранение меняет только черновик.</span>
            <button type="button" className="button primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim()}>
              {saveMutation.isPending ? <LoaderCircle className="spin" /> : <Save />} Сохранить группу
            </button>
          </div>
        </> : <div className="telephony-stage-empty"><Network /><span><b>Выберите группу</b><small>Или создайте новую.</small></span></div>}
      </section>
    </div>

    <RoutingHistoryPanel
      entries={historyQuery.data || []}
      isLoading={historyQuery.isLoading}
      error={historyQuery.error instanceof Error ? historyQuery.error.message : historyQuery.isError ? 'История временно недоступна' : null}
      memberNames={routeMemberNames}
    />

    {applyPreview && <div
      className="telephony-route-preview-modal telephony-route-apply-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Подтверждение применения маршрутизации"
      onMouseDown={() => {
        if (!applyMutation.isPending) setApplyPreview(null);
      }}
    >
      <section className="surface telephony-route-apply-confirm" onMouseDown={event => event.stopPropagation()}>
        <div className="telephony-route-section-head">
          <div><small>ПРИМЕНЕНИЕ СХЕМЫ</small><h3>Подтвердите изменение FreeSWITCH</h3></div>
          <button
            type="button"
            className="icon-button"
            title="Закрыть"
            disabled={applyMutation.isPending}
            onClick={() => setApplyPreview(null)}
          ><X /></button>
        </div>

        <RoutingDiffView preview={applyPreview} memberNames={routeMemberNames} compact />

        <div className="telephony-route-validation warning">
          <CircleAlert />
          <span>
            <b>Будет изменён боевой dialplan FreeSWITCH</b>
            <small>Перед применением API ещё раз проверит активные звонки. При ошибке выполняется автоматический rollback.</small>
          </span>
        </div>

        {applyError && <div className="telephony-route-validation error">
          <CircleAlert />
          <span><b>Применение не выполнено</b><small>{applyError}</small></span>
        </div>}

        <div className="telephony-route-apply-actions">
          <button
            type="button"
            className="button"
            disabled={applyMutation.isPending}
            onClick={() => setApplyPreview(null)}
          >Отмена</button>
          <button
            type="button"
            className="button primary"
            disabled={applyMutation.isPending}
            onClick={() => applyMutation.mutate(applyPreview.draftRevision)}
          >
            {applyMutation.isPending ? <LoaderCircle className="spin" /> : <ShieldCheck />}
            {applyMutation.isPending ? 'Применяем схему…' : 'Применить схему'}
          </button>
        </div>
      </section>
    </div>}

    {preview && <div
      className="telephony-route-preview-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Предпросмотр маршрутизации"
      onMouseDown={() => setPreview(null)}
    >
      <section className="surface telephony-route-preview" onMouseDown={event => event.stopPropagation()}>
        <div className="telephony-route-section-head">
          <div><small>ПРЕДПРОСМОТР</small><h3>Черновик и рабочий маршрут</h3></div>
          <button type="button" className="icon-button" title="Закрыть" onClick={() => setPreview(null)}><X /></button>
        </div>
        {preview.validation.errors.length > 0 && <div className="telephony-route-validation error"><CircleAlert /><span><b>Применение заблокировано</b>{preview.validation.errors.map(item => <small key={item}>{item}</small>)}</span></div>}
        {preview.validation.warnings.length > 0 && <div className="telephony-route-validation warning"><CircleAlert /><span><b>Предупреждения</b>{preview.validation.warnings.map(item => <small key={item}>{item}</small>)}</span></div>}
        {!preview.validation.errors.length && <div className="telephony-route-validation ok"><CheckCircle2 /><span><b>Конфигурация прошла проверку</b><small>{preview.validation.chain.join(' → ') || 'Маршрут не определён'}</small><small>Получателей в техническом dial-string: {preview.technical?.bridge.targetCount ?? 0}; общий тайм-аут: {preview.technical?.bridge.maxWaitSeconds ?? 0} сек.</small></span></div>}
        <RoutingDiffView preview={preview} memberNames={routeMemberNames} />
        {preview.technical?.dialplan && <details className="telephony-route-dialplan"><summary>Технический preview FreeSWITCH XML</summary><pre>{preview.technical.dialplan}</pre></details>}
      </section>
    </div>}
  </div>;
}
